import React from 'react';
import { Typography, TypographyProps, SxProps, Theme } from '@mui/material';
import { colors, typographyVariants } from '../../tokens';

export interface OverlineProps extends Omit<TypographyProps, 'variant'> {
  /**
   * The content to display
   */
  children: React.ReactNode;
  /**
   * Color variant
   * @default 'primary'
   */
  colorVariant?: 'primary' | 'secondary';
  /**
   * Custom styles to apply
   */
  sx?: SxProps<Theme>;
}

/**
 * Overline - Uppercase text for category labels and eyebrows
 * 
 * Use cases:
 * - Category labels
 * - Eyebrow text above headings
 * - Section identifiers
 * - Badge-like text
 * - Navigation labels
 * 
 * @example
 * ```tsx
 * <Overline>Featured</Overline>
 * <Overline colorVariant="secondary">Category</Overline>
 * <Overline>Step 1 of 5</Overline>
 * ```
 */
export const Overline: React.FC<OverlineProps> = ({ 
  children, 
  colorVariant = 'primary',
  sx = {}, 
  ...props 
}) => {
  const colorMap = {
    primary: colors.text.primary,
    secondary: colors.text.tertiary,
  };

  return (
    <Typography
      sx={{
        fontFamily: typographyVariants.overline.fontFamily,
        fontWeight: typographyVariants.overline.fontWeight,
        fontSize: typographyVariants.overline.fontSize,
        lineHeight: typographyVariants.overline.lineHeight,
        textTransform: typographyVariants.overline.textTransform,
        letterSpacing: typographyVariants.overline.letterSpacing,
        color: colorMap[colorVariant],
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};
